<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="480" persistent
        content-class="ms-dialog-wrap">
        <div class="ms-dialog">
            <div class="ms-alert__icon">
                <v-icon size="28">mdi-delete-alert-outline</v-icon>
            </div>

            <h2 class="ms-alert__title">למחוק את קריאה #{{ item.request_number }}?</h2>

            <p class="ms-alert__text">
                הקריאה תוסר מהרשימה אך תישמר במערכת ותהיה ניתנת לשחזור.
                הקבצים המצורפים לא נמחקים.
            </p>

            <dl class="ms-alert__summary">
                <div class="ms-alert__row">
                    <dt>לקוח</dt>
                    <dd>{{ customerName }}</dd>
                </div>
                <div class="ms-alert__row">
                    <dt>מספר קו</dt>
                    <dd class="ms-mono">{{ item.line_number || '—' }}</dd>
                </div>
                <div class="ms-alert__row">
                    <dt>סטטוס</dt>
                    <dd>
                        <span class="ms-status-pill" :class="`ms-status-pill--${statusModifier(item.status)}`">
                            <span class="ms-status-pill__dot"></span>
                            {{ statusLabel(item.status) }}
                        </span>
                    </dd>
                </div>
            </dl>

            <div v-if="item.status === 'in_progress'" class="ms-alert__warn">
                <v-icon size="16">mdi-alert-outline</v-icon>
                הקריאה נמצאת בביצוע — ודא שהטיפול מול הספק הופסק.
            </div>

            <div v-if="errorText" class="ms-error">
                <v-icon size="16">mdi-alert-circle-outline</v-icon>
                {{ errorText }}
            </div>

            <div class="ms-alert__actions">
                <button type="button" class="ms-btn ms-btn--ghost" :disabled="deleting" @click="close">ביטול</button>
                <button type="button" class="ms-btn ms-btn--danger" :disabled="deleting" @click="deleteItem">
                    <v-progress-circular v-if="deleting" indeterminate size="15" width="2" color="#fff" />
                    <v-icon v-else size="16">mdi-delete-outline</v-icon>
                    {{ deleting ? 'מוחק…' : 'מחיקת הקריאה' }}
                </button>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import ApiServices from '@/services/api.service'
import SRConsts from '@/util/serviceRequestsConsts'

export default {
    props: {
        value: { type: Boolean, default: false },
        item: { type: Object, default: () => ({}) },
    },
    data: () => ({
        deleting: false,
        errorText: '',
    }),
    computed: {
        customerName() {
            const c = this.item.customer_id;
            if (!c) return '—';
            return `${c.name || ''} ${c.family || ''}`.trim() || '—';
        },
    },
    methods: {
        statusLabel: SRConsts.statusLabel,
        statusModifier: SRConsts.statusModifier,

        async deleteItem() {
            this.errorText = '';
            try {
                this.deleting = true;
                const token = localStorage.getItem("token");
                // _id עובר ב-query ולא ב-body: לא כל שרת/פרוקסי מפרסר גוף בבקשת DELETE
                const api = process.env.VUE_APP_BASE_URL
                    + `/service_requests/delete_service_request?_id=${encodeURIComponent(this.item._id)}`;

                const res = await fetch(api, ApiServices.requestOptions("DELETE", "", token));
                const jsonObject = await res.json();
                this.deleting = false;

                if (res.status >= 400) {
                    this.errorText = (jsonObject && jsonObject.message) || 'המחיקה נכשלה. נסה שוב.';
                    return;
                }
                this.$emit('itemDeleted', this.item, 'הקריאה נמחקה בהצלחה', 'green');
                this.$emit('input', false);
            } catch (error) {
                this.deleting = false;
                this.errorText = 'שגיאת תקשורת: ' + error;
            }
        },
        close() {
            this.$emit('input', false);
        },
    },
}
</script>

<style scoped>
.ms-dialog {
    --m-navy: #0d2c6d;
    --m-blue: #1976d2;
    --m-blue-soft: rgba(25, 118, 210, 0.10);
    --m-border: #e3e8ef;
    --m-border-soft: #eef2f7;
    --m-text: #0f172a;
    --m-text-soft: #475569;
    --m-text-mute: #94a3b8;
    --m-green: #16804b;
    --m-green-soft: rgba(22, 128, 75, 0.10);
    --m-orange: #c0691c;
    --m-orange-soft: #fff3df;
    --m-red: #c74747;
    --m-red-soft: rgba(199, 71, 71, 0.10);
    --m-ease: cubic-bezier(0.22, 1, 0.36, 1);
    padding: 24px 22px 18px;
    background: #fff;
    text-align: center;
    direction: rtl;
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, 'Segoe UI', sans-serif;
}

.ms-alert__icon {
    display: grid;
    place-items: center;
    width: 58px;
    height: 58px;
    margin: 0 auto 14px;
    border-radius: 50%;
    background: var(--m-red-soft);
    color: var(--m-red);
}

.ms-alert__icon ::v-deep .v-icon {
    color: var(--m-red) !important;
}

.ms-alert__title {
    margin: 0 0 7px;
    color: var(--m-text);
    font-size: 1.08rem;
    font-weight: 800;
}

.ms-alert__text {
    margin: 0 0 16px;
    color: var(--m-text-soft);
    font-size: 0.87rem;
    line-height: 1.6;
}

.ms-alert__summary {
    margin: 0 0 14px;
    padding: 11px 13px;
    border-radius: 11px;
    background: #f7f9fc;
    border: 1px solid var(--m-border-soft);
    text-align: right;
}

.ms-alert__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 5px 0;
    border-bottom: 1px dashed var(--m-border-soft);
}

.ms-alert__row:last-child {
    border-bottom: none;
}

.ms-alert__row dt {
    color: var(--m-text-mute);
    font-size: 0.79rem;
    font-weight: 600;
}

.ms-alert__row dd {
    margin: 0;
    color: var(--m-text);
    font-size: 0.86rem;
    font-weight: 700;
}

.ms-mono {
    direction: ltr;
    font-feature-settings: "tnum" 1;
}

.ms-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.77rem;
    font-weight: 700;
}

.ms-status-pill__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
}

.ms-status-pill--open {
    background: var(--m-blue-soft);
    color: var(--m-blue);
}

.ms-status-pill--progress {
    background: var(--m-orange-soft);
    color: var(--m-orange);
}

.ms-status-pill--completed {
    background: var(--m-green-soft);
    color: var(--m-green);
}

.ms-status-pill--canceled {
    background: var(--m-red-soft);
    color: var(--m-red);
}

.ms-alert__warn {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 14px;
    padding: 9px 12px;
    border-radius: 10px;
    background: var(--m-orange-soft);
    border: 1px solid rgba(192, 105, 28, 0.2);
    color: var(--m-orange);
    font-size: 0.82rem;
    font-weight: 700;
    text-align: right;
}

.ms-alert__warn ::v-deep .v-icon {
    color: var(--m-orange) !important;
}

.ms-error {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 14px;
    padding: 9px 12px;
    border-radius: 10px;
    background: var(--m-red-soft);
    border: 1px solid rgba(199, 71, 71, 0.22);
    color: var(--m-red);
    font-size: 0.83rem;
    font-weight: 700;
    text-align: right;
}

.ms-error ::v-deep .v-icon {
    color: var(--m-red) !important;
}

.ms-alert__actions {
    display: flex;
    gap: 9px;
    padding-top: 6px;
}

.ms-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 1;
    padding: 9px 16px;
    border-radius: 9px;
    font-family: inherit;
    font-size: 0.86rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-btn:disabled {
    opacity: 0.55;
    cursor: default;
}

.ms-btn--danger {
    background: var(--m-red);
    border: 1px solid var(--m-red);
    color: #fff;
}

.ms-btn--danger:hover:not(:disabled) {
    background: #b83c3c;
    box-shadow: 0 6px 14px -5px rgba(199, 71, 71, 0.6);
}

.ms-btn--danger ::v-deep .v-icon {
    color: #fff !important;
}

.ms-btn--ghost {
    background: transparent;
    border: 1px solid var(--m-border);
    color: var(--m-text-soft);
}

.ms-btn--ghost:hover:not(:disabled) {
    background: #f1f5f9;
    color: var(--m-text);
}
</style>